import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import api from '../../services/api';

/* =============================================================
   Lumix Óticas — Comparativo de Unidades Clean
   Padrão visual: Google-like, DPI pequeno, multiempresa real.
   Sem lojas fixas, sem vendedores hardcoded, sem IP/baseURL fixa e sem forçar empresaId pelo front.
============================================================= */

const COLORS = [
  { main: '#1a73e8', bg: '#eef5ff', border: '#d2e3fc' },
  { main: '#188038', bg: '#f1f8f4', border: '#cce8d5' },
  { main: '#b06000', bg: '#fff7ed', border: '#fed7aa' },
  { main: '#7e22ce', bg: '#faf5ff', border: '#e9d5ff' },
  { main: '#d93025', bg: '#fef2f2', border: '#fecaca' },
  { main: '#0f766e', bg: '#f0fdfa', border: '#ccfbf1' }
];

const PRESETS = [
  { id: 'today', label: 'Hoje' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês atual' },
  { id: 'lastMonth', label: 'Mês passado' },
  { id: 'last30', label: '30 dias' }
];

const Icons = {
  Calendar: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Store: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 6h6M9 10h6M9 14h6M9 18h6" />
    </svg>
  ),
  TrendingUp: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 7 13 16 9 12 2 19" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  ),
  User: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Trophy: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
      <path d="M17 5h2a2 2 0 0 1 0 4h-2" />
      <path d="M7 5H5a2 2 0 0 0 0 4h2" />
    </svg>
  ),
  Refresh: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.5 2v6h-6" />
      <path d="M21.34 15.57a10 10 0 1 1-.59-9.21L21.5 8" />
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Receipt: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2Z" />
      <path d="M8 7h8M8 12h8M8 17h5" />
    </svg>
  ),
  Chart: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="5" />
      <rect x="12" y="8" width="3" height="9" />
      <rect x="17" y="5" width="3" height="12" />
    </svg>
  )
};

/* =============================================================
   Helpers
============================================================= */

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value || 0));

const formatCompactCurrency = (value) => {
  const n = Number(value || 0);

  if (Math.abs(n) >= 1000000) return `R$ ${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toFixed(0)}k`;

  return `R$ ${n.toFixed(0)}`;
};

/*
  Importante para multiempresa:
  Este componente NÃO força empresaId pelo front.
  O escopo correto deve vir pelo token no backend.
  Assim admin/grupo/multiempresa não perde vendas por causa de localStorage incompleto.
*/

const resolverArrayApi = (data, fallbackKey = '') => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.dados)) return data.dados;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.registros)) return data.registros;

  if (fallbackKey && Array.isArray(data?.[fallbackKey])) return data[fallbackKey];

  if (Array.isArray(data?.empresas)) return data.empresas;
  if (Array.isArray(data?.ordens)) return data.ordens;
  if (Array.isArray(data?.ordensServico)) return data.ordensServico;
  if (Array.isArray(data?.ordens_servico)) return data.ordens_servico;

  if (data?.data && typeof data.data === 'object') {
    const nested = resolverArrayApi(data.data, fallbackKey);

    if (nested.length) return nested;
  }

  return [];
};

const normalizarId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const mesmoId = (a, b) => {
  if (!a || !b) return false;
  return String(a) === String(b);
};

const normalizarTexto = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const safeId = (value = '') =>
  String(value || 'serie')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '_');

const initials = (name = '') => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'NI';

  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
};

const formatDateBR = (dateString) => {
  if (!dateString) return '—';

  try {
    const datePart = String(dateString).split('T')[0];
    const [year, month, day] = datePart.split('-');

    if (!year || !month || !day) return String(dateString);

    return `${day}/${month}/${year}`;
  } catch {
    return String(dateString);
  }
};

const parseMoneyValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value || '')
    .replace(/[^0-9,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');

  const number = Number(cleaned);

  return Number.isFinite(number) ? number : 0;
};

const getDataOS = (os = {}) =>
  os.dataCompra ||
  os.dataVenda ||
  os.data ||
  os.dataEntrada ||
  os.createdAt ||
  os.created_at ||
  os.updatedAt ||
  '';

const getOsItems = (os = {}) => {
  if (Array.isArray(os.itens) && os.itens.length) return os.itens;

  if (os.produto || os.receita || os.paciente) {
    return [
      {
        paciente: os.paciente || os.cliente || {},
        produto: os.produto || {},
        receita: os.receita || { od: {}, oe: {} },
        quantidade: 1,
        valorTotal: os.valorTotal || os.valor || 0
      }
    ];
  }

  return [];
};

const getValorOS = (os = {}) => {
  const camposDiretos = [
    os.valorTotal,
    os.total,
    os.valor,
    os.valorVenda,
    os.valorFinal,
    os.totalVenda,
    os.totalGeral,
    os.financeiro?.valorTotal,
    os.financeiro?.total,
    os.resumo?.valorTotal,
    os.resumo?.total
  ];

  for (const campo of camposDiretos) {
    const valor = parseMoneyValue(campo);

    if (valor > 0) return valor;
  }

  const totalItens = getOsItems(os).reduce((acc, item) => {
    const valorTotalItem = parseMoneyValue(
      item.valorTotal ||
      item.total ||
      item.subtotal ||
      item.produto?.valorTotal ||
      item.produto?.preco
    );

    if (valorTotalItem > 0) return acc + valorTotalItem;

    const valorUnitario = parseMoneyValue(item.valorUnitario || item.produto?.valorUnitario);
    const quantidade = Number(item.quantidade || 1) || 1;

    return acc + valorUnitario * quantidade;
  }, 0);

  if (totalItens > 0) {
    return Number(totalItens.toFixed(2));
  }

  if (Array.isArray(os.pagamentos)) {
    const totalPago = os.pagamentos.reduce((acc, p) => acc + parseMoneyValue(p.valor), 0);

    if (totalPago > 0) return Number(totalPago.toFixed(2));
  }

  return 0;
};


const getVendedorNome = (os = {}) => {
  const vendedor = os.vendedor;

  if (!vendedor) return 'Não informado';
  if (typeof vendedor === 'object') return vendedor.nomeCompleto || vendedor.nome || vendedor.usuario || 'Não informado';

  return String(vendedor);
};

const getEmpresaCodigo = (empresa = {}) =>
  String(empresa.codigoLoja || empresa.loja || empresa.unidade || empresa._id || empresa.id || '').trim();

const getEmpresaLabel = (empresa = {}) => {
  if (!empresa) return '';

  const nome = empresa.nome || empresa.razaoSocial || 'Empresa';
  const unidade = empresa.unidade && empresa.unidade !== 'Matriz' ? ` · ${empresa.unidade}` : '';

  return `${nome}${unidade}`;
};

const getEmpresaCurta = (empresa = {}) => {
  if (!empresa) return 'Sem loja';

  return empresa.unidade || empresa.codigoLoja || empresa.nome || 'Sem loja';
};

const getEmpresaIdOS = (os = {}) =>
  normalizarId(
    os.empresaId ||
      os.empresa?._id ||
      os.empresa?.id ||
      os.empresa ||
      ''
  );

const encontrarEmpresaDaOS = (os = {}, empresas = []) => {
  const osEmpresaId = getEmpresaIdOS(os);
  const loja = String(os.loja || '').trim();
  const lojaNormalizada = normalizarTexto(loja);

  return (
    empresas.find((empresa) => mesmoId(empresa._id, osEmpresaId) || mesmoId(empresa.id, osEmpresaId)) ||
    empresas.find((empresa) => normalizarTexto(getEmpresaCodigo(empresa)) === lojaNormalizada) ||
    empresas.find((empresa) => normalizarTexto(empresa.unidade) === lojaNormalizada) ||
    empresas.find((empresa) => normalizarTexto(empresa.nome) === lojaNormalizada) ||
    null
  );
};

const colorForIndex = (index, empresa = {}) => {
  const corEmpresa = empresa.corPrimaria || empresa.primaryColor || empresa.cor || '';

  if (corEmpresa && corEmpresa !== '#0f172a') {
    return {
      ...COLORS[index % COLORS.length],
      main: corEmpresa
    };
  }

  return COLORS[index % COLORS.length];
};

const getToday = () => new Date().toISOString().split('T')[0];

const getMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};


const getPresetRange = (preset) => {
  const now = new Date();
  let start = new Date(now);
  let end = new Date(now);

  if (preset === 'today') {
    start = new Date(now);
    end = new Date(now);
  }

  if (preset === 'week') {
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;

    start = new Date(now);
    start.setDate(now.getDate() + diffToMon);
    end = new Date(now);
  }

  if (preset === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now);
  }

  if (preset === 'lastMonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0);
  }

  if (preset === 'last30') {
    start = new Date(now);
    start.setDate(now.getDate() - 29);
    end = new Date(now);
  }

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};

/* =============================================================
   Componentes pequenos
============================================================= */

function LoadingPanel() {
  return (
    <div className="compare-loading">
      <div className="thin-loader" />
      <span>Analisando unidades...</span>
    </div>
  );
}

function KpiCell({ icon, label, value, help, tone = 'neutral' }) {
  return (
    <div className={`kpi-cell ${tone}`}>
      <div className="kpi-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      {help ? <small>{help}</small> : null}
    </div>
  );
}

function StoreBadge({ loja }) {
  return (
    <span
      className="store-badge"
      style={{
        '--store-main': loja.colors.main,
        '--store-bg': loja.colors.bg,
        '--store-border': loja.colors.border
      }}
    >
      <Icons.Store />
      {loja.nomeCurto}
    </span>
  );
}

function ShareBar({ lojas }) {
  return (
    <div className="share-box">
      <div className="share-labels">
        {lojas.map((loja) => (
          <span key={loja.id} style={{ color: loja.colors.main }}>
            {loja.nomeCurto} · {loja.share.toFixed(1)}%
          </span>
        ))}
      </div>

      <div className="share-track">
        {lojas.map((loja) => (
          <i
            key={loja.id}
            style={{
              width: `${loja.share}%`,
              background: loja.colors.main
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* =============================================================
   Componente principal
============================================================= */

export default function ComparativoLojas({ onBack }) {
  const [ordens, setOrdens] = useState([]);
  const [empresasApi, setEmpresasApi] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dataInicio, setDataInicio] = useState(getMonthStart());
  const [dataFim, setDataFim] = useState(getToday());
  const [activePreset, setActivePreset] = useState('month');
  const [empresaFiltro, setEmpresaFiltro] = useState('Todas');

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [resOrdens, resEmpresas] = await Promise.all([
        api.get('/ordens-servico'),
        api.get('/empresas', {
          params: {
            comResumo: true
          }
        })
      ]);

      setOrdens(resolverArrayApi(resOrdens.data, 'ordens'));

      const empresasAtivas = resolverArrayApi(resEmpresas.data, 'empresas')
        .filter((empresa) => empresa.ativo !== false);

      setEmpresasApi(empresasAtivas);
    } catch (error) {
      console.error('[ComparativoLojas] Erro ao buscar dados:', error);
      setOrdens([]);
      setEmpresasApi([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyPreset = (preset) => {
    const range = getPresetRange(preset);

    setDataInicio(range.start);
    setDataFim(range.end);
    setActivePreset(preset);
  };

  const empresasComparaveis = useMemo(() => {
    const cadastradas = empresasApi.filter((empresa) => empresa.ativo !== false);
    const resultado = [...cadastradas];

    const chavesExistentes = new Set(
      cadastradas.flatMap((empresa) => [
        normalizarId(empresa._id || empresa.id),
        normalizarTexto(getEmpresaCodigo(empresa)),
        normalizarTexto(empresa.unidade),
        normalizarTexto(empresa.nome)
      ]).filter(Boolean)
    );

    ordens.forEach((os) => {
      if (encontrarEmpresaDaOS(os, cadastradas)) return;

      const loja = String(os.loja || getEmpresaIdOS(os) || '').trim();

      if (!loja) return;

      const chave = normalizarTexto(loja);

      if (!chave || chavesExistentes.has(chave)) return;

      chavesExistentes.add(chave);

      /*
        Fallback para OS antigas/legadas:
        se a OS tem loja, mas não bateu com empresas cadastradas,
        criamos uma unidade virtual só para o comparativo não esconder venda.
      */
      resultado.push({
        _id: `fallback-${safeId(chave)}`,
        id: `fallback-${safeId(chave)}`,
        nome: loja,
        unidade: loja,
        codigoLoja: loja,
        ativo: true,
        fallback: true
      });
    });

    return resultado;
  }, [empresasApi, ordens]);

  const lojasBase = useMemo(() => {
    return empresasComparaveis.map((empresa, index) => {
      const colors = colorForIndex(index, empresa);
      const id = normalizarId(empresa._id || empresa.id) || getEmpresaCodigo(empresa);

      return {
        id,
        empresa,
        nome: getEmpresaLabel(empresa),
        nomeCurto: getEmpresaCurta(empresa),
        codigo: getEmpresaCodigo(empresa),
        colors,
        bruto: 0,
        osQtd: 0,
        osValidasQtd: 0,
        ticketMedio: 0,
        share: 0,
        vendedores: {}
      };
    });
  }, [empresasComparaveis]);

  const stats = useMemo(() => {
    const lojasMap = {};

    lojasBase.forEach((loja) => {
      lojasMap[loja.id] = {
        ...loja,
        vendedores: {}
      };
    });

    const dias = {};
    const vendedoresGlobais = {};

    let totalValidContracts = 0;
    let totalBrutoGeral = 0;
    let totalOs = 0;
    let osSemEmpresa = 0;

    ordens.forEach((os) => {
      const dataStr = getDataOS(os);
      if (!dataStr) return;

      const dataOS = String(dataStr).split('T')[0];

      if (dataInicio && dataOS < dataInicio) return;
      if (dataFim && dataOS > dataFim) return;

      const empresaAlvo = encontrarEmpresaDaOS(os, empresasComparaveis);

      if (!empresaAlvo) {
        osSemEmpresa += 1;
        return;
      }

      const targetId = normalizarId(empresaAlvo._id || empresaAlvo.id) || getEmpresaCodigo(empresaAlvo);

      if (empresaFiltro !== 'Todas' && targetId !== empresaFiltro) return;

      if (!lojasMap[targetId]) {
        const index = Object.keys(lojasMap).length;
        const colors = colorForIndex(index, empresaAlvo);

        lojasMap[targetId] = {
          id: targetId,
          empresa: empresaAlvo,
          nome: getEmpresaLabel(empresaAlvo),
          nomeCurto: getEmpresaCurta(empresaAlvo),
          codigo: getEmpresaCodigo(empresaAlvo),
          colors,
          bruto: 0,
          osQtd: 0,
          osValidasQtd: 0,
          ticketMedio: 0,
          share: 0,
          vendedores: {}
        };
      }

      const loja = lojasMap[targetId];
      const valorBruto = getValorOS(os);
      const vendedorNome = getVendedorNome(os);

      loja.bruto += valorBruto;
      loja.osQtd += 1;
      totalOs += 1;

      if (valorBruto > 0) {
        loja.osValidasQtd += 1;
        totalValidContracts += 1;
      }

      totalBrutoGeral += valorBruto;

      if (!dias[dataOS]) {
        dias[dataOS] = {
          dataOriginal: dataOS
        };
      }

      if (!dias[dataOS][loja.nomeCurto]) {
        dias[dataOS][loja.nomeCurto] = 0;
      }

      dias[dataOS][loja.nomeCurto] += valorBruto;

      const vendedorKey = `${normalizarTexto(vendedorNome)}-${targetId}`;

      if (!loja.vendedores[vendedorKey]) {
        loja.vendedores[vendedorKey] = {
          id: vendedorKey,
          nome: vendedorNome,
          lojaId: targetId,
          lojaNome: loja.nomeCurto,
          lojaCor: loja.colors,
          bruto: 0,
          os: 0
        };
      }

      loja.vendedores[vendedorKey].bruto += valorBruto;
      loja.vendedores[vendedorKey].os += 1;

      if (!vendedoresGlobais[vendedorKey]) {
        vendedoresGlobais[vendedorKey] = {
          id: vendedorKey,
          nome: vendedorNome,
          lojaId: targetId,
          lojaNome: loja.nomeCurto,
          lojaCor: loja.colors,
          bruto: 0,
          os: 0
        };
      }

      vendedoresGlobais[vendedorKey].bruto += valorBruto;
      vendedoresGlobais[vendedorKey].os += 1;
    });

    const lojas = Object.values(lojasMap)
      .map((loja) => ({
        ...loja,
        ticketMedio: loja.osValidasQtd > 0 ? loja.bruto / loja.osValidasQtd : 0,
        share: totalBrutoGeral > 0 ? (loja.bruto / totalBrutoGeral) * 100 : 0
      }))
      .sort((a, b) => b.bruto - a.bruto);

    const lojasComVenda = lojas.filter((loja) => loja.bruto > 0);
    const lojaLider = lojasComVenda[0] || null;

    const rankingVendedores = Object.values(vendedoresGlobais)
      .map((vendedor) => ({
        ...vendedor,
        ticketMedio: vendedor.os > 0 ? vendedor.bruto / vendedor.os : 0
      }))
      .sort((a, b) => b.bruto - a.bruto)
      .slice(0, 12);

    const chartData = Object.values(dias)
      .sort((a, b) => new Date(a.dataOriginal) - new Date(b.dataOriginal))
      .map((dia) => {
        const [, mes, day] = dia.dataOriginal.split('-');

        const point = {
          ...dia,
          dataFormatada: `${day}/${mes}`
        };

        lojas.forEach((loja) => {
          if (!point[loja.nomeCurto]) {
            point[loja.nomeCurto] = 0;
          }
        });

        return point;
      });

    const ticketMedioGeral = totalValidContracts > 0 ? totalBrutoGeral / totalValidContracts : 0;

    return {
      lojas,
      totalBrutoGeral,
      totalValidContracts,
      totalOs,
      ticketMedioGeral,
      rankingVendedores,
      chartData,
      lojaLider,
      osSemEmpresa
    };
  }, [ordens, empresasComparaveis, lojasBase, dataInicio, dataFim, empresaFiltro]);

  const pieData = useMemo(
    () => stats.lojas
      .filter((loja) => loja.bruto > 0)
      .map((loja) => ({
        id: loja.id,
        nome: loja.nomeCurto,
        valor: Number(loja.bruto.toFixed(2)),
        share: Number(loja.share.toFixed(1)),
        color: loja.colors.main
      })),
    [stats.lojas]
  );

  const bubbleData = useMemo(
    () => stats.lojas
      .filter((loja) => loja.osQtd > 0)
      .map((loja) => ({
        id: loja.id,
        nome: loja.nomeCurto,
        ticket: Number(loja.ticketMedio.toFixed(2)),
        receita: Number(loja.bruto.toFixed(2)),
        vendas: loja.osValidasQtd,
        color: loja.colors.main
      })),
    [stats.lojas]
  );

  const barData = useMemo(
    () => stats.lojas
      .filter((loja) => loja.bruto > 0)
      .slice(0, 8)
      .map((loja) => ({
        id: loja.id,
        nome: loja.nomeCurto,
        receita: Number(loja.bruto.toFixed(2)),
        ticket: Number(loja.ticketMedio.toFixed(2)),
        color: loja.colors.main
      })),
    [stats.lojas]
  );

  if (loading) {
    return (
      <div className="compare-page">
        <GlobalStyles />
        <LoadingPanel />
      </div>
    );
  }

  if (lojasBase.length === 0 && ordens.length === 0) {
    return (
      <div className="compare-page">
        <GlobalStyles />
        <div className="empty-state">
          <Icons.Store />
          <strong>Nenhuma venda encontrada</strong>
          <span>Nenhuma OS retornou da API para montar o comparativo da rede.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="compare-page">
      <GlobalStyles />

      <header className="compare-top">
        <div className="compare-top-left">
          {onBack ? (
            <button type="button" className="plain-btn" onClick={onBack}>
              <Icons.ArrowLeft />
              Voltar
            </button>
          ) : null}

          <div>
            <span className="kicker">Lumix Óticas</span>
            <h1>Comparativo de unidades</h1>
            <p>Desempenho por ótica, participação da rede e ranking de consultores.</p>
          </div>
        </div>

        <button type="button" className="plain-icon-btn" onClick={fetchData} title="Atualizar">
          <Icons.Refresh />
        </button>
      </header>

      <section className="filters-line">
        <select
          value={empresaFiltro}
          onChange={(e) => setEmpresaFiltro(e.target.value)}
        >
          <option value="Todas">Todas as óticas</option>
          {lojasBase.map((loja) => (
            <option key={loja.id} value={loja.id}>
              {loja.nome}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={dataInicio}
          onChange={(e) => {
            setDataInicio(e.target.value);
            setActivePreset(null);
          }}
        />

        <input
          type="date"
          value={dataFim}
          onChange={(e) => {
            setDataFim(e.target.value);
            setActivePreset(null);
          }}
        />

        <div className="preset-group">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={activePreset === preset.id ? 'active' : ''}
              onClick={() => applyPreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </section>

      <section className="kpi-strip">
        <KpiCell
          icon={<Icons.Receipt />}
          label="Receita da rede"
          value={formatCurrency(stats.totalBrutoGeral)}
          help={`${stats.totalValidContracts} venda(s) válidas`}
          tone="blue"
        />

        <KpiCell
          icon={<Icons.Chart />}
          label="Ticket médio"
          value={formatCurrency(stats.ticketMedioGeral)}
          help="Baseado em vendas com valor"
          tone="green"
        />

        <KpiCell
          icon={<Icons.Store />}
          label="Óticas ativas"
          value={stats.lojas.filter((loja) => loja.osQtd > 0).length}
          help={`${stats.lojas.length} unidade(s) no grupo`}
          tone="neutral"
        />

        <KpiCell
          icon={<Icons.Trophy />}
          label="Líder do período"
          value={stats.lojaLider?.nomeCurto || '—'}
          help={stats.lojaLider ? formatCurrency(stats.lojaLider.bruto) : 'Sem vendas'}
          tone="orange"
        />
      </section>

      <section className="share-section">
        <div className="share-head">
          <div>
            <h2>Participação das unidades</h2>
            <p>{formatDateBR(dataInicio)} até {formatDateBR(dataFim)}</p>
          </div>
        </div>

        <ShareBar lojas={stats.lojas} />
      </section>

      {stats.osSemEmpresa > 0 ? (
        <section className="warning-banner">
          <span>{stats.osSemEmpresa} OS entraram por fallback de loja porque não bateram com uma empresa cadastrada. Confira empresaId, loja ou codigoLoja depois.</span>
        </section>
      ) : null}

      <section className="store-grid">
        {stats.lojas.map((loja) => {
          const isLeader = stats.lojaLider?.id === loja.id && loja.bruto > 0;

          return (
            <article
              key={loja.id}
              className={`store-card ${isLeader ? 'leader' : ''}`}
              style={{
                '--store-main': loja.colors.main,
                '--store-bg': loja.colors.bg,
                '--store-border': loja.colors.border
              }}
            >
              <div className="store-card-top">
                <StoreBadge loja={loja} />

                {isLeader ? (
                  <span className="leader-tag">
                    <Icons.Trophy />
                    Líder
                  </span>
                ) : null}
              </div>

              <strong className="store-revenue">{formatCurrency(loja.bruto)}</strong>

              <div className="store-metrics">
                <div>
                  <span>Ticket</span>
                  <strong>{formatCurrency(loja.ticketMedio)}</strong>
                </div>

                <div>
                  <span>Válidas</span>
                  <strong>{loja.osValidasQtd}</strong>
                </div>

                <div>
                  <span>Total OS</span>
                  <strong>{loja.osQtd}</strong>
                </div>
              </div>

              <div className="store-progress">
                <i style={{ width: `${loja.share}%` }} />
              </div>
            </article>
          );
        })}
      </section>

      <section className="visual-grid">
        <article className="visual-panel pie-panel">
          <div className="panel-head">
            <div>
              <h2><Icons.Chart /> Participação da receita</h2>
              <p>Quanto cada unidade representa no faturamento da rede.</p>
            </div>
          </div>

          <div className="visual-chart pie-chart-wrap">
            {pieData.length ? (
              <ResponsiveContainer width="100%" height={290}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="valor"
                    nameKey="nome"
                    cx="50%"
                    cy="48%"
                    innerRadius={62}
                    outerRadius={96}
                    paddingAngle={2}
                  >
                    {pieData.map((item) => (
                      <Cell key={item.id} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, item) => [formatCurrency(value), item?.payload?.nome || name]} />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 10, fontWeight: 700 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-mini compact">Sem dados para o gráfico de participação.</div>
            )}
          </div>
        </article>

        <article className="visual-panel bubble-panel">
          <div className="panel-head">
            <div>
              <h2><Icons.TrendingUp /> Eficiência por unidade</h2>
              <p>Ticket médio x receita. O tamanho da bolha representa vendas válidas.</p>
            </div>
          </div>

          <div className="visual-chart">
            {bubbleData.length ? (
              <ResponsiveContainer width="100%" height={290}>
                <ScatterChart margin={{ top: 18, right: 18, bottom: 12, left: 0 }}>
                  <CartesianGrid stroke="#edf0f3" />
                  <XAxis
                    type="number"
                    dataKey="ticket"
                    name="Ticket médio"
                    tickFormatter={formatCompactCurrency}
                    tick={{ fill: '#80868b', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="number"
                    dataKey="receita"
                    name="Receita"
                    tickFormatter={formatCompactCurrency}
                    tick={{ fill: '#80868b', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <ZAxis type="number" dataKey="vendas" range={[90, 720]} name="Vendas" />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => {
                      if (name === 'Ticket médio' || name === 'Receita') return formatCurrency(value);
                      return value;
                    }}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.nome || ''}
                  />
                  <Scatter data={bubbleData}>
                    {bubbleData.map((item) => (
                      <Cell key={item.id} fill={item.color} fillOpacity={0.78} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-mini compact">Sem dados para o gráfico de bolhas.</div>
            )}
          </div>
        </article>
      </section>

      <section className="visual-panel revenue-bars-panel">
        <div className="panel-head">
          <div>
            <h2><Icons.Store /> Receita por unidade</h2>
            <p>Comparação direta das unidades com venda no período.</p>
          </div>
        </div>

        <div className="visual-chart bars-chart-wrap">
          {barData.length ? (
            <ResponsiveContainer width="100%" height={Math.max(260, barData.length * 42)}>
              <BarChart data={barData} layout="vertical" margin={{ top: 8, right: 24, left: 18, bottom: 8 }}>
                <CartesianGrid stroke="#edf0f3" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={formatCompactCurrency}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#80868b', fontSize: 10 }}
                />
                <YAxis
                  type="category"
                  dataKey="nome"
                  axisLine={false}
                  tickLine={false}
                  width={92}
                  tick={{ fill: '#5f6368', fontSize: 10, fontWeight: 700 }}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="receita" name="Receita" radius={[0, 7, 7, 0]}>
                  {barData.map((item) => (
                    <Cell key={item.id} fill={item.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-mini compact">Sem receita para comparar.</div>
          )}
        </div>
      </section>

      <main className="content-grid">
        <section className="chart-panel">
          <div className="panel-head">
            <div>
              <h2><Icons.TrendingUp /> Evolução diária</h2>
              <p>Receita diária por unidade no período filtrado.</p>
            </div>
          </div>

          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height={292}>
              <AreaChart data={stats.chartData} margin={{ top: 12, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  {stats.lojas.map((loja) => (
                    <linearGradient key={loja.id} id={`grad-${safeId(loja.nomeCurto)}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={loja.colors.main} stopOpacity={0.22} />
                      <stop offset="95%" stopColor={loja.colors.main} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>

                <CartesianGrid stroke="#edf0f3" vertical={false} />
                <XAxis
                  dataKey="dataFormatada"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5f6368', fontSize: 10, fontWeight: 600 }}
                  minTickGap={18}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#80868b', fontSize: 10 }}
                  tickFormatter={formatCompactCurrency}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: '#202124', fontWeight: 700 }}
                  contentStyle={{
                    border: '1px solid #dadce0',
                    borderRadius: 12,
                    boxShadow: 'none',
                    fontSize: 12
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} iconType="circle" />

                {stats.lojas.map((loja) => (
                  <Area
                    key={loja.id}
                    type="monotone"
                    dataKey={loja.nomeCurto}
                    stroke={loja.colors.main}
                    strokeWidth={2}
                    fill={`url(#grad-${safeId(loja.nomeCurto)})`}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="ranking-panel">
          <div className="panel-head">
            <div>
              <h2><Icons.User /> Top consultores</h2>
              <p>Ranking por receita vendida no período.</p>
            </div>
          </div>

          <div className="ranking-list">
            {stats.rankingVendedores.length ? (
              stats.rankingVendedores.map((vendedor, index) => {
                const loja = stats.lojas.find((item) => item.id === vendedor.lojaId);
                const maiorValor = stats.rankingVendedores[0]?.bruto || 1;
                const percent = maiorValor > 0 ? Math.min(100, (vendedor.bruto / maiorValor) * 100) : 0;

                return (
                  <div key={vendedor.id} className="rank-row">
                    <span className="rank-pos">{index + 1}</span>

                    <div
                      className="rank-avatar"
                      style={{
                        color: vendedor.lojaCor.main,
                        background: vendedor.lojaCor.bg,
                        borderColor: vendedor.lojaCor.border
                      }}
                    >
                      {initials(vendedor.nome)}
                    </div>

                    <div className="rank-main">
                      <div className="rank-line">
                        <div>
                          <strong>{vendedor.nome}</strong>
                          <span>{vendedor.lojaNome} · {vendedor.os} venda(s)</span>
                        </div>

                        <div className="rank-value">
                          <strong>{formatCurrency(vendedor.bruto)}</strong>
                          <span>{formatCurrency(vendedor.ticketMedio)} ticket</span>
                        </div>
                      </div>

                      <div className="rank-progress">
                        <i style={{ width: `${percent}%`, background: loja?.colors.main || vendedor.lojaCor.main }} />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-mini">Nenhuma venda encontrada no período.</div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

/* =============================================================
   CSS
============================================================= */

function GlobalStyles() {
  return (
    <style>{`
      :root {
        --cmp-bg: #f8fafd;
        --cmp-surface: #ffffff;
        --cmp-surface-2: #f1f3f4;
        --cmp-text: #202124;
        --cmp-muted: #5f6368;
        --cmp-soft: #80868b;
        --cmp-border: #dadce0;
        --cmp-border-soft: #edf0f3;
        --cmp-blue: #1a73e8;
        --cmp-green: #188038;
        --cmp-red: #d93025;
        --cmp-orange: #b06000;
        --cmp-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif;
      }

      * {
        box-sizing: border-box;
      }

      .compare-page {
        min-height: 100vh;
        padding: 18px;
        color: var(--cmp-text);
        background: var(--cmp-bg);
        font-family: var(--cmp-font);
        -webkit-font-smoothing: antialiased;
        text-rendering: geometricPrecision;
      }

      .compare-top {
        margin-bottom: 12px;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 14px;
      }

      .compare-top-left {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }

      .kicker {
        display: block;
        margin-bottom: 2px;
        color: var(--cmp-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .04em;
        text-transform: uppercase;
      }

      .compare-top h1 {
        margin: 0;
        color: var(--cmp-text);
        font-size: 26px;
        line-height: 1.1;
        font-weight: 650;
        letter-spacing: -.035em;
      }

      .compare-top p {
        margin: 4px 0 0;
        color: var(--cmp-muted);
        font-size: 13px;
      }

      .plain-btn,
      .plain-icon-btn {
        height: 34px;
        border: 1px solid var(--cmp-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: var(--cmp-muted);
        background: #fff;
        font-size: 12px;
        font-weight: 650;
        font-family: inherit;
        cursor: pointer;
      }

      .plain-btn {
        padding: 0 11px;
      }

      .plain-icon-btn {
        width: 34px;
        padding: 0;
        flex-shrink: 0;
      }

      .plain-btn:hover,
      .plain-icon-btn:hover {
        color: var(--cmp-text);
        background: var(--cmp-surface-2);
      }

      .filters-line {
        margin-bottom: 10px;
        padding: 9px;
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: minmax(220px, 280px) 138px 138px minmax(340px, 1fr);
        gap: 8px;
        background: #fff;
      }

      .filters-line select,
      .filters-line input {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--cmp-border);
        border-radius: 999px;
        outline: 0;
        color: var(--cmp-text);
        background: #fff;
        font-size: 12px;
        font-family: inherit;
      }

      .filters-line select:focus,
      .filters-line input:focus {
        border-color: var(--cmp-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .preset-group {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 6px;
        overflow-x: auto;
      }

      .preset-group button {
        height: 30px;
        padding: 0 10px;
        border: 1px solid var(--cmp-border);
        border-radius: 999px;
        color: var(--cmp-muted);
        background: #fff;
        font-size: 11px;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
        white-space: nowrap;
      }

      .preset-group button.active {
        color: var(--cmp-blue);
        border-color: #d2e3fc;
        background: #f8fbff;
      }

      .kpi-strip {
        margin-bottom: 10px;
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        overflow: hidden;
        background: #fff;
      }

      .kpi-cell {
        min-height: 82px;
        padding: 12px;
        border-right: 1px solid var(--cmp-border-soft);
        display: grid;
        grid-template-columns: 28px 1fr;
        grid-template-rows: auto auto auto;
        column-gap: 8px;
        align-content: center;
        background: #fff;
      }

      .kpi-cell:last-child {
        border-right: 0;
      }

      .kpi-icon {
        grid-row: 1 / span 3;
        width: 28px;
        height: 28px;
        border: 1px solid var(--cmp-border);
        border-radius: 9px;
        display: grid;
        place-items: center;
        color: var(--cmp-blue);
        background: #f8fafd;
      }

      .kpi-cell.blue .kpi-icon {
        color: var(--cmp-blue);
        background: #eef5ff;
        border-color: #d2e3fc;
      }

      .kpi-cell.green .kpi-icon {
        color: var(--cmp-green);
        background: #f1f8f4;
        border-color: #cce8d5;
      }

      .kpi-cell.orange .kpi-icon {
        color: var(--cmp-orange);
        background: #fff7ed;
        border-color: #fed7aa;
      }

      .kpi-cell span {
        color: var(--cmp-muted);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .02em;
        text-transform: uppercase;
      }

      .kpi-cell strong {
        margin-top: 3px;
        color: var(--cmp-text);
        font-size: 18px;
        line-height: 1.15;
        font-weight: 700;
        letter-spacing: -.025em;
      }

      .kpi-cell small {
        margin-top: 3px;
        color: var(--cmp-soft);
        font-size: 11px;
        font-weight: 500;
      }

      .share-section {
        margin-bottom: 10px;
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        overflow: hidden;
        background: #fff;
      }

      .share-head {
        min-height: 52px;
        padding: 12px;
        border-bottom: 1px solid var(--cmp-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .share-head h2,
      .panel-head h2 {
        margin: 0;
        color: var(--cmp-text);
        font-size: 14px;
        font-weight: 750;
        letter-spacing: -.01em;
      }

      .share-head p,
      .panel-head p {
        margin: 3px 0 0;
        color: var(--cmp-muted);
        font-size: 11.5px;
      }

      .share-box {
        padding: 12px;
      }

      .share-labels {
        margin-bottom: 9px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
      }

      .share-labels span {
        font-size: 11.5px;
        font-weight: 750;
      }

      .share-track {
        height: 8px;
        border-radius: 999px;
        display: flex;
        overflow: hidden;
        background: #edf0f3;
      }

      .share-track i {
        min-width: 0;
        height: 100%;
      }

      .warning-banner {
        margin-bottom: 10px;
        padding: 10px 12px;
        border: 1px solid #fde68a;
        border-radius: 13px;
        color: #92400e;
        background: #fffbeb;
        font-size: 12px;
        font-weight: 650;
      }

      .store-grid {
        margin-bottom: 10px;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(245px, 1fr));
        gap: 10px;
      }

      .store-card {
        padding: 12px;
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        display: grid;
        gap: 12px;
        background: #fff;
      }

      .store-card.leader {
        border-color: var(--store-border);
        box-shadow: inset 3px 0 0 var(--store-main);
      }

      .store-card-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      .store-badge {
        max-width: 100%;
        padding: 4px 8px;
        border: 1px solid var(--store-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--store-main);
        background: var(--store-bg);
        font-size: 11px;
        font-weight: 800;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .leader-tag {
        padding: 4px 7px;
        border: 1px solid var(--store-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--store-main);
        background: var(--store-bg);
        font-size: 10px;
        font-weight: 850;
        text-transform: uppercase;
      }

      .store-revenue {
        color: var(--cmp-text);
        font-size: 25px;
        line-height: 1.05;
        font-weight: 650;
        letter-spacing: -.035em;
      }

      .store-metrics {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 6px;
      }

      .store-metrics div {
        padding: 8px;
        border: 1px solid var(--cmp-border-soft);
        border-radius: 10px;
        display: grid;
        gap: 2px;
        background: #fff;
      }

      .store-metrics span {
        color: var(--cmp-muted);
        font-size: 10px;
        font-weight: 750;
        text-transform: uppercase;
      }

      .store-metrics strong {
        color: var(--cmp-text);
        font-size: 12px;
        font-weight: 750;
      }

      .store-progress {
        height: 5px;
        border-radius: 999px;
        overflow: hidden;
        background: #edf0f3;
      }

      .store-progress i {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: var(--store-main);
      }

      .content-grid {
        display: grid;
        grid-template-columns: minmax(0, 1.45fr) minmax(330px, .55fr);
        gap: 10px;
      }

      .chart-panel,
      .ranking-panel {
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        overflow: hidden;
        background: #fff;
      }

      .panel-head {
        min-height: 54px;
        padding: 12px;
        border-bottom: 1px solid var(--cmp-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .panel-head h2 {
        display: flex;
        align-items: center;
        gap: 7px;
      }

      .chart-wrap {
        padding: 10px 10px 4px;
      }

      .ranking-list {
        max-height: 356px;
        overflow: auto;
      }

      .ranking-list::-webkit-scrollbar,
      .preset-group::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      .ranking-list::-webkit-scrollbar-thumb,
      .preset-group::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: rgba(95, 99, 104, .28);
      }

      .rank-row {
        min-height: 64px;
        padding: 9px 12px;
        border-bottom: 1px solid var(--cmp-border-soft);
        display: grid;
        grid-template-columns: 24px 36px minmax(0, 1fr);
        align-items: center;
        gap: 9px;
      }

      .rank-pos {
        color: var(--cmp-muted);
        font-size: 12px;
        font-weight: 800;
        text-align: center;
      }

      .rank-avatar {
        width: 36px;
        height: 36px;
        border: 1px solid;
        border-radius: 999px;
        display: grid;
        place-items: center;
        font-size: 12px;
        font-weight: 850;
      }

      .rank-main {
        min-width: 0;
        display: grid;
        gap: 6px;
      }

      .rank-line {
        min-width: 0;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
      }

      .rank-line strong {
        display: block;
        color: var(--cmp-text);
        font-size: 12.5px;
        font-weight: 750;
      }

      .rank-line span {
        display: block;
        margin-top: 2px;
        color: var(--cmp-muted);
        font-size: 10.5px;
        font-weight: 600;
      }

      .rank-value {
        text-align: right;
        white-space: nowrap;
      }

      .rank-progress {
        height: 4px;
        border-radius: 999px;
        overflow: hidden;
        background: #edf0f3;
      }

      .rank-progress i {
        display: block;
        height: 100%;
        border-radius: inherit;
      }

      .empty-mini,
      .empty-state,
      .compare-loading {
        min-height: 320px;
        padding: 24px;
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        display: grid;
        place-items: center;
        align-content: center;
        gap: 9px;
        color: var(--cmp-muted);
        background: #fff;
        font-size: 13px;
        text-align: center;
      }

      .empty-state strong {
        color: var(--cmp-text);
        font-size: 16px;
      }

      .thin-loader {
        width: 190px;
        height: 3px;
        border-radius: 999px;
        overflow: hidden;
        background: #e8eaed;
        position: relative;
      }

      .thin-loader::after {
        content: "";
        position: absolute;
        inset: 0;
        width: 40%;
        border-radius: inherit;
        background: var(--cmp-blue);
        animation: loader-move 1.1s linear infinite;
      }

      @keyframes loader-move {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(260%); }
      }

      @media (max-width: 1100px) {
        .filters-line {
          grid-template-columns: 1fr 1fr;
        }

        .preset-group {
          grid-column: 1 / -1;
        }

        .kpi-strip {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .content-grid {
          grid-template-columns: 1fr;
        }
      }



      .visual-grid {
        margin-bottom: 10px;
        display: grid;
        grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr);
        gap: 10px;
      }

      .visual-panel {
        margin-bottom: 10px;
        border: 1px solid var(--cmp-border);
        border-radius: 13px;
        overflow: hidden;
        background: #fff;
      }

      .visual-grid .visual-panel {
        margin-bottom: 0;
      }

      .visual-chart {
        min-height: 290px;
        padding: 8px 10px 2px;
      }

      .pie-chart-wrap {
        display: grid;
        place-items: center;
      }

      .bars-chart-wrap {
        min-height: 260px;
        padding: 8px 12px 6px;
      }

      .empty-mini.compact {
        min-height: 230px;
        border: 0;
        border-radius: 0;
      }

      @media (max-width: 980px) {
        .visual-grid {
          grid-template-columns: 1fr;
        }
      }
      @media (max-width: 680px) {
        .compare-page {
          padding: 10px;
        }

        .compare-top {
          align-items: stretch;
          flex-direction: column;
        }

        .compare-top-left {
          flex-direction: column;
          gap: 8px;
        }

        .filters-line {
          grid-template-columns: 1fr;
        }

        .preset-group {
          grid-column: auto;
        }

        .kpi-strip {
          grid-template-columns: 1fr;
        }

        .kpi-cell {
          border-right: 0;
          border-bottom: 1px solid var(--cmp-border-soft);
        }

        .kpi-cell:last-child {
          border-bottom: 0;
        }

        .store-metrics {
          grid-template-columns: 1fr;
        }

        .rank-line {
          flex-direction: column;
        }

        .rank-value {
          text-align: left;
        }
      }
    `}</style>
  );
}
